import { motion } from "framer-motion";
import { Crown, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const PLAN_CARDS = [
  { id: "monthly", label: "Monthly", price: "$24.99", per: "per month", note: "Full flexibility" },
  {
    id: "quarterly",
    label: "Quarterly",
    price: "$59.99",
    per: "for 3 months",
    note: "≈ $20/month — most popular",
    featured: true,
  },
  {
    id: "yearly",
    label: "Yearly",
    price: "$149.99",
    per: "for 12 months",
    note: "≈ $12.50/month — best value",
  },
];

export const PlanCard = ({ plan, index, onCheckout, loadingPlan }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className={`border p-8 flex items-center justify-between gap-6 ${
      plan.featured ? "bg-[#7A3B3F] text-[#FAF9F6] border-[#7A3B3F]" : "bg-[#F3F0EA] border-[#E5E0D8]"
    }`}
    data-testid={`plan-card-${plan.id}`}
  >
    <div>
      <h3 className="font-serif text-2xl">{plan.label}</h3>
      <p className={`mt-1 text-sm ${plan.featured ? "text-[#FAF9F6]/70" : "text-[#8A8A8A]"}`}>
        {plan.note}
      </p>
      <p className="mt-4">
        <span className="font-serif text-3xl">{plan.price}</span>{" "}
        <span className={`text-sm ${plan.featured ? "text-[#FAF9F6]/70" : "text-[#8A8A8A]"}`}>
          {plan.per}
        </span>
      </p>
    </div>
    <button
      data-testid={`plan-checkout-${plan.id}`}
      onClick={() => onCheckout(plan.id)}
      disabled={loadingPlan !== null}
      className={`px-6 py-3 whitespace-nowrap transition-colors duration-300 disabled:opacity-50 ${
        plan.featured
          ? "bg-[#FAF9F6] text-[#7A3B3F] hover:bg-[#F3F0EA]"
          : "bg-[#7A3B3F] text-[#FAF9F6] hover:bg-[#5D2B2E]"
      }`}
    >
      {loadingPlan === plan.id ? "Redirecting…" : "Choose"}
    </button>
  </motion.div>
);

export const PaymentStatusBanner = ({ state }) => {
  const navigate = useNavigate();
  if (!state) return null;
  if (state === "checking") {
    return (
      <div className="mt-10 bg-[#F3F0EA] border border-[#E5E0D8] p-8 flex items-center gap-4" data-testid="payment-checking">
        <Loader2 className="w-5 h-5 animate-spin text-[#7A3B3F]" />
        <p className="text-[#5C5C5C]">Confirming your payment…</p>
      </div>
    );
  }
  if (state === "paid") {
    return (
      <div className="mt-10 bg-[#4A5D4E] text-[#FAF9F6] p-8" data-testid="payment-success">
        <p className="font-serif text-2xl">Payment successful. You're Premium.</p>
        <p className="mt-2 text-[#FAF9F6]/80">
          Unlimited likes, admirers revealed, and every filter unlocked.
        </p>
        <button
          data-testid="payment-success-discover-button"
          onClick={() => navigate("/discover")}
          className="mt-6 border border-[#FAF9F6] px-6 py-3 hover:bg-[#FAF9F6] hover:text-[#4A5D4E] transition-colors duration-300"
        >
          Back to Discover
        </button>
      </div>
    );
  }
  return (
    <div className="mt-10 bg-[#F3F0EA] border border-[#9A3B3B] p-8" data-testid="payment-failed">
      <p className="text-[#9A3B3B]">
        {state === "expired"
          ? "This payment session expired. Please try again."
          : "We couldn't confirm your payment. If you were charged, it will reflect shortly — try refreshing."}
      </p>
    </div>
  );
};

export const PremiumActiveBanner = ({ user }) => (
  <div className="mt-10 bg-[#F3F0EA] border border-[#C19A6B] p-8" data-testid="premium-active-banner">
    <p className="font-serif text-xl flex items-center gap-2">
      <Crown className="w-5 h-5 text-[#C19A6B]" strokeWidth={1.5} /> You're a Premium member
    </p>
    <p className="mt-2 text-sm text-[#5C5C5C]">
      Active until {user.premium_until ? new Date(user.premium_until).toLocaleDateString() : "—"}.
      Purchasing again extends your membership.
    </p>
  </div>
);
